import React, { FC, ReactNode, useCallback, useEffect } from "react";
import { useSelector } from "react-redux";
import { Checkbox, Description, Icon, IconToggle, IconTypesKeys } from "@evergis/ui";
import { useTranslation } from "react-i18next";

import {
  LayerControl,
  LayerInformation,
  LayerItem,
  LayerSubtitle,
  LayerTitle,
  LayerTitleText,
  Notice,
  TableControl,
  TableIconContainer,
  TableInformation,
  TableItem,
  TableStructure,
  TableSubtitle,
  TableTitle,
  TableTitleContainer,
} from "./styled";

import { getUsername } from "../../../../../ducks/user";
import { useResourcesLimits } from "../../hooks/useResourcesLimits";

import { LayerStructureInfo, TableStructureInfo } from "./hooks/useGetTableStructure";
import { TableStructureLayer, TableStructureTable } from "./hooks/useTableStructureControls";
import { ProjectCopyOptionsProps } from "./CopyProjectOptions";

// eslint-disable-next-line max-lines-per-function
export const ProjectCopyTableStructure: FC<ProjectCopyOptionsProps> = ({
  deps,
  tableStructure,
  tableStructureControls,
  limits,
}) => {
  const { t } = useTranslation("dialogs");
  const currentUser: string = useSelector(getUsername);
  const { getStateByName, updateSelected, updateFiltered } = tableStructureControls;

  const {
    tablesTotalCount,
    setTablesTotalCount,
    layersTotalCount,
    setLayersTotalCount,
    isInitialTablesLimit,
    isInitialLayersLimit,
    isTablesLimitExceeded,
    isLayersLimitExceeded,
  } = useResourcesLimits(limits);

  useEffect(() => {
    if (!limits || !tableStructureControls) {
      return;
    }

    const { treeState } = tableStructureControls;
    const tablesSelected: number = treeState && treeState.filter(item => item.selected).length;
    const layersSelected: number =
      treeState &&
      treeState.reduce((acc: number, curr: TableStructureTable) => {
        return acc + curr.layers.filter(item => item.selected).length;
      }, 0);

    setTablesTotalCount(limits.currentTableCount + tablesSelected);
    setLayersTotalCount(limits.currentServiceCount + layersSelected);
  }, [limits, tableStructureControls]);

  const processLimits = useCallback(() => {
    if (isInitialLayersLimit) {
      return (
        <Notice info>
          {t("copyResource.layersLimitsExceeded")} ({limits.currentServiceCount} {t("copyResource.of")}{" "}
          {limits && limits.maxServicesCount}
          ). {t("copyResource.originalLayersAndTablesMessage")}
        </Notice>
      );
    }

    if ((!isInitialTablesLimit && isTablesLimitExceeded) || (!isInitialLayersLimit && isLayersLimitExceeded)) {
      return (
        <Notice warning>
          {t("copyResource.notAllWillBeCopied")}
          {isLayersLimitExceeded ? ` ${t("copyResource.layers")}` : ""}
          {isTablesLimitExceeded && isLayersLimitExceeded ? ` ${t("copyResource.and")} ` : ""}
          {isTablesLimitExceeded ? ` ${t("copyResource.tables")}` : ""}. {t("copyResource.limitsExceeded")} (
          {isLayersLimitExceeded ? `${limits && limits.maxServicesCount} ${t("copyResource.ofLayers")}` : ""}
          {isTablesLimitExceeded && isLayersLimitExceeded ? ", " : ""}
          {isTablesLimitExceeded ? `${limits && limits.maxTablesCount} ${t("copyResource.ofTables")}` : ""})
        </Notice>
      );
    }

    return <Notice success>{t("copyResource.resourcesThatCouldBeCopied")}:</Notice>;
  }, [isTablesLimitExceeded, isLayersLimitExceeded, tablesTotalCount, layersTotalCount, limits, t]);

  if (!deps || !tableStructure.length) {
    return null;
  }

  return (
    <>
      {processLimits()}
      {!isInitialLayersLimit &&
        tableStructure.map((table: TableStructureInfo): ReactNode => {
          const tableState = getStateByName(table.name) as TableStructureTable;
          const isTableSelected: boolean = tableState && tableState.selected;
          const isDisabled: boolean =
            tableState && tableState.layers && !tableState.layers.some((layer: TableStructureLayer) => layer.selected);

          return (
            <TableStructure key={table.name}>
              <TableItem>
                <TableInformation>
                  <TableIconContainer>
                    <Icon kind={`geom_${table.geometries}` as IconTypesKeys} />
                  </TableIconContainer>
                  <TableTitleContainer>
                    <TableTitle>{table.alias}</TableTitle>
                    <TableSubtitle>{table.owner !== currentUser ? table.owner : " "}</TableSubtitle>
                  </TableTitleContainer>
                </TableInformation>
                <TableControl>
                  {!isInitialTablesLimit ? (
                    <Checkbox
                      disabled={isDisabled}
                      checked={isTableSelected}
                      label={t("copyResource.copyTable")}
                      onChange={() => {
                        updateSelected(table.name, !isTableSelected, "table", table.name);
                      }}
                    />
                  ) : (
                    <Description>{t("copyResource.tablesLimitExceeded")}</Description>
                  )}
                </TableControl>
              </TableItem>
              {table.layers.map((layer: LayerStructureInfo) => {
                const layerState = getStateByName(layer.name) as TableStructureLayer;
                const isLayerSelected: boolean = layerState && layerState.selected;
                const isLayerFiltered: boolean = layerState && layerState.filtered;

                return (
                  <LayerItem key={layer.name} isFilterable={layer.isFilterable}>
                    <LayerInformation>
                      <LayerTitle>
                        {layer.isFilterable ? (
                          <IconToggle
                            kind="filter"
                            isSelected={isLayerFiltered}
                            disabled={isDisabled || !isTableSelected}
                            onClick={() => updateFiltered(layer.name, !isLayerFiltered)}
                            title={t("copyResource.copyFilteredFeaturesOnly")}
                          />
                        ) : null}
                        <LayerTitleText>{layer.alias}</LayerTitleText>
                      </LayerTitle>
                      <LayerSubtitle>{layer.owner !== currentUser ? layer.owner : " "}</LayerSubtitle>
                    </LayerInformation>
                    <LayerControl>
                      <Checkbox
                        checked={isLayerSelected}
                        label={t("copyResource.copyLayer")}
                        onChange={() => {
                          updateSelected(layer.name, !isLayerSelected, "layer", table.name);
                        }}
                      />
                    </LayerControl>
                  </LayerItem>
                );
              })}
            </TableStructure>
          );
        })}
    </>
  );
};
